import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFeaturedPosts } from "../../../hooks/useBlogPosts";

function extractIntroduction(
  content: string,
  maxLength = 180
) {
  // Try to extract the Introduction section from HTML content
  const introMatch = content.match(
    /<h2>Introduction<\/h2>\s*<p>([\s\S]*?)<\//i
  );
  let intro = introMatch ? introMatch[1] : "";
  if (!intro) {
    // fallback: first <p> or plain text
    const pMatch = content.match(/<p>([\s\S]*?)<\//i);
    intro = pMatch
      ? pMatch[1]
      : content.replace(/<[^>]+>/g, "");
  }
  // Truncate to about 2 lines (roughly 180 chars)
  if (intro.length > maxLength) {
    intro =
      intro
        .slice(0, maxLength)
        .trim()
        .replace(/\s+\S*$/, "") + "...";
  }
  return intro;
}

function slugify(title: string) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const FeaturedArticlesSection: React.FC = () => {
  const navigate = useNavigate();
  const { posts: featuredPosts, loading } = useFeaturedPosts();
  const [isButtonHovered, setIsButtonHovered] =
    useState(false);
  // Only show featured articles
  const mainArticle = featuredPosts[0];
  const gridArticles = featuredPosts.slice(1, 7);

  if (loading) {
    return (
      <section className="w-full flex flex-col items-center pt-16 md:pt-24 pb-16 md:pb-20 bg-white dark:bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </section>
    );
  }

  return (
    <section className="w-full flex flex-col items-center pt-16 md:pt-24 pb-16 md:pb-20 bg-white dark:bg-black transition-colors duration-500">
      <div className="w-full flex items-center justify-center py-2 pb-16 transition-colors duration-500">
        <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600 mx-8" />
        <span className="text-4xl font-bold bg-gradient-to-r from-blue-700 to-blue-400 bg-clip-text text-transparent drop-shadow-[0_2px_16px_rgba(30,64,175,0.18)] whitespace-nowrap">
          <span className="bg-gradient-to-r from-[#FE6019] to-[#ffb347] bg-clip-text text-transparent drop-shadow-[0_2px_16px_rgba(254,96,25,0.25)]">
            FEATURES
          </span>{" "}
          POSTS
        </span>
        <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600 mx-8" />
      </div>
      <div className="max-w-6xl w-full mx-auto flex flex-col items-center px-4">
        {/* Articles layout */}
        <div className="w-full flex flex-col md:flex-row gap-8">
          {/* Main article */}
          {mainArticle && (
            <div className="flex-1 min-w-[260px] max-w-xl border-r border-gray-200 dark:border-gray-800 pr-8">
              <div
                className="cursor-pointer group"
                onClick={() =>
                  navigate(
                    `/blog/${slugify(mainArticle.title)}`
                  )
                }
              >
                <img
                  src={mainArticle.image}
                  alt={mainArticle.title}
                  className="w-full h-64 object-cover rounded-xl mb-6 group-hover:scale-105 transition-transform duration-300"
                />
                <div className="uppercase text-xs text-gray-500 dark:text-gray-400 font-semibold mb-2 tracking-wider">
                  {mainArticle.topic} • {mainArticle.date}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                  {mainArticle.title}
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4 text-base">
                  {extractIntroduction(mainArticle.content)}
                </p>
              </div>
            </div>
          )}
          {/* Grid articles */}
          <div className="flex-[1.2] grid grid-cols-1 sm:grid-cols-2 gap-6">
            {gridArticles.map((post) => (
              <div
                key={post.id}
                className="flex flex-col cursor-pointer group"
                onClick={() =>
                  navigate(`/blog/${slugify(post.title)}`)
                }
              >
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-32 object-cover rounded-xl mb-3 group-hover:scale-105 transition-transform duration-300"
                />
                <div className="uppercase text-xs text-gray-500 dark:text-gray-400 font-semibold mb-1 tracking-wider">
                  {post.topic} • {post.date}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 leading-snug">
                  {post.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
        {/* See more button */}
        <button
          className={`relative flex items-center overflow-hidden font-semibold text-base sm:text-lg shadow-md transition-all duration-300 rounded-lg px-6 py-3 mt-10
            ${
              isButtonHovered
                ? "bg-black dark:bg-white text-white dark:text-black"
                : "bg-orange-500 dark:bg-orange-600 text-white"
            }
          `}
          onClick={() => navigate("/blog")}
          onMouseEnter={() => setIsButtonHovered(true)}
          onMouseLeave={() => setIsButtonHovered(false)}
        >
          <span className="z-10 transition-colors duration-300">
            SEE MORE
          </span>
          <span
            className={`flex items-center transition-all duration-300 ml-2 ${
              isButtonHovered
                ? "translate-x-0 opacity-100"
                : "translate-x-6 opacity-0"
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 12h14M12 5l7 7-7 7"
              />
            </svg>
          </span>
        </button>
      </div>
    </section>
  );
};

export default FeaturedArticlesSection;
